var __accessCheck = (obj, member, msg) => {
  if (!member.has(obj))
    throw TypeError("Cannot " + msg);
};
var __privateGet = (obj, member, getter) => {
  __accessCheck(obj, member, "read from private field");
  return getter ? getter.call(obj) : member.get(obj);
};
var __privateAdd = (obj, member, value) => {
  if (member.has(obj))
    throw TypeError("Cannot add the same private member more than once");
  member instanceof WeakSet ? member.add(obj) : member.set(obj, value);
};
var __privateSet = (obj, member, value, setter) => {
  __accessCheck(obj, member, "write to private field");
  setter ? setter.call(obj, value) : member.set(obj, value);
  return value;
};
var __privateMethod = (obj, member, method) => {
  __accessCheck(obj, member, "access private method");
  return method;
};

// src/extenders/faq-dropdowns.js
var _animationClasses, _onToggleCallbacks, _initializeQuestions, initializeQuestions_fn, _setupKeyboardSupport, setupKeyboardSupport_fn, _updateAnswerClasses, updateAnswerClasses_fn, _notifyToggleCallbacks, notifyToggleCallbacks_fn;
var FaqDropdowns = class extends HTMLElement {
  constructor() {
    super();
    /**
     * Initialize event listeners for all FAQ questions
     * @private
     */
    __privateAdd(this, _initializeQuestions);
    /**
     * Set up keyboard support for a question element
     * @param {HTMLElement} question - The question element to set up keyboard support for
     * @private
     */
    __privateAdd(this, _setupKeyboardSupport);
    /**
     * Update the classes on the answer panel based on its state
     * @param {HTMLElement} answerPanel - The answer panel element
     * @param {boolean} isExpanded - Whether the panel should be expanded
     * @private
     */
    __privateAdd(this, _updateAnswerClasses);
    /**
     * Notify all registered callbacks about a toggle event
     * @param {HTMLElement} question - The question element being toggled
     * @param {HTMLElement} answer - The answer element being toggled
     * @param {boolean} isExpanded - Whether the FAQ is being expanded or collapsed
     * @private
     */
    __privateAdd(this, _notifyToggleCallbacks);
    /**
     * @type {FaqAnimationClasses}
     * @private
     */
    __privateAdd(this, _animationClasses, {
      show: ["opacity-100", "pb-3", "lg:pb-4", "max-h-fit"],
      hide: ["opacity-0", "max-h-0"]
    });
    /**
     * @type {OnToggleCallback[]}
     * @private
     */
    __privateAdd(this, _onToggleCallbacks, []);
    __privateMethod(this, _initializeQuestions, initializeQuestions_fn).call(this);
  }
  /**
   * Toggle the visibility of a FAQ question's answer
   * @param {HTMLElement} question - The question element to toggle
   * @public
   */
  toggleQuestion(question) {
    const isExpanded = question.getAttribute("aria-expanded") === "true";
    const newState = !isExpanded;
    question.setAttribute("aria-expanded", newState.toString());
    const answerId = question.getAttribute("aria-controls");
    const answerPanel = document.getElementById(answerId);
    if (answerPanel) {
      __privateMethod(this, _updateAnswerClasses, updateAnswerClasses_fn).call(this, answerPanel, newState);
      __privateMethod(this, _notifyToggleCallbacks, notifyToggleCallbacks_fn).call(this, question, answerPanel, newState);
    }
  }
  /**
   * Add a callback to be called when a FAQ is toggled
   * @param {OnToggleCallback} callback - The callback function to add
   * @public
   */
  addToggleCallback(callback) {
    __privateGet(this, _onToggleCallbacks).push(callback);
  }
  /**
   * Remove a previously added toggle callback
   * @param {OnToggleCallback} callback - The callback function to remove
   * @public
   */
  removeToggleCallback(callback) {
    __privateSet(this, _onToggleCallbacks, __privateGet(this, _onToggleCallbacks).filter((cb) => cb !== callback));
  }
};
_animationClasses = new WeakMap();
_onToggleCallbacks = new WeakMap();
_initializeQuestions = new WeakSet();
initializeQuestions_fn = function() {
  const faqQuestions = this.querySelectorAll("[aria-controls]");
  faqQuestions.forEach((question) => {
    question.addEventListener("click", () => this.toggleQuestion(question));
    __privateMethod(this, _setupKeyboardSupport, setupKeyboardSupport_fn).call(this, question);
  });
};
_setupKeyboardSupport = new WeakSet();
setupKeyboardSupport_fn = function(question) {
  question.addEventListener("keydown", (event) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      this.toggleQuestion(question);
    }
  });
};
_updateAnswerClasses = new WeakSet();
updateAnswerClasses_fn = function(answerPanel, isExpanded) {
  if (isExpanded) {
    answerPanel.classList.add(...__privateGet(this, _animationClasses).show);
    answerPanel.classList.remove(...__privateGet(this, _animationClasses).hide);
  } else {
    answerPanel.classList.remove(...__privateGet(this, _animationClasses).show);
    answerPanel.classList.add(...__privateGet(this, _animationClasses).hide);
  }
};
_notifyToggleCallbacks = new WeakSet();
notifyToggleCallbacks_fn = function(question, answer, isExpanded) {
  __privateGet(this, _onToggleCallbacks).forEach((callback) => {
    callback(question, answer, isExpanded);
  });
};
if (!window.customElements.get("faq-dropdowns")) {
  window.customElements.define("faq-dropdowns", FaqDropdowns);
}
